import type { WindowLocation } from '@reach/router';
import { Location } from '@reach/router';
import React, { use } from 'react';

import { ClubCmsContent } from 'src/content/club-content-consumer';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import { useLink } from 'src/custom-hooks/use-link';
import { getClubDisplayValues } from 'src/enums/build/build-type';
import { clubNameDependingOnBuildTypeAndLanguage } from 'src/helpers/club-helper';
import { colors } from 'src/styling/constants';
import mq from 'src/styling/media-queries';
import { TranslatedStaticText } from 'src/translators/static-text-translator';

import { WebsiteLink } from '../../common/website-link';

import {
  DesktopSubNavMainPageLinks,
  DesktopSubNavSecondaryPageLinks,
} from './desktop-subnav-menu-links';

const subNavWrapperStyling = (styling: Styling, isDL: boolean) =>
  mq({
    minHeight: '3.5rem',
    flex: 1,
    color: isDL ? styling.colors.capeCod : styling.colors.headerNavigationText,
    fontSize: isDL ? '1.1875rem' : '1rem',
    display: ['none', 'flex'],
    borderBottom: isDL ? `1px solid ${styling.colors.headerBorder}` : null,
    padding: [null, '0 2rem'],
    textAlign: 'center',
    alignItems: 'center',
    position: 'relative',
    backgroundColor: isDL ? styling.colors.lightBackground : 'unset',
  });

const atUnitTextStyling = (styling: Styling, isDL: boolean) => [
  styling.common.headerStyle,
  styling.fonts.regular,
  {
    padding: isDL ? '0.875rem 2rem 0.875rem 1rem' : '1rem 2rem 1rem 1rem',
    color: colors.lightSilver,
    lineHeight: isDL ? '145%' : 'unset',
  },
];

type DesktopNavLinkProps = {
  location: WindowLocation;
  path: string;
  displayText: React.ReactNode | string;
};

export const DesktopNavLink: React.FC<DesktopNavLinkProps> = ({ location, path, displayText }) => {
  const { styling, isDL } = use(SiteContext);
  const cmsContent = use(ClubCmsContent);
  const { isPageDisabledInCms, isHighlight } = useLink({
    toPath: path,
    location,
    enabledClubPages: cmsContent?.pages,
  });

  if (isPageDisabledInCms) {
    return null;
  }

  const oldActiveState = isHighlight ? styling.fonts.bold : styling.fonts.regular;
  const newActiveState = styling.fonts.regular;
  const linkStyles = isDL
    ? {
        'margin': '0.875rem 1rem',
        'cursor': 'pointer',
        'borderBottom': '1px solid',
        'borderBottomColor': isHighlight ? styling.colors.capeCod : 'transparent',
        'lineHeight': '145%',
        '&:hover': {
          color: styling.colors.camelot,
          borderBottomColor: styling.colors.camelot,
        },
      }
    : {
        padding: '1rem',
        cursor: 'pointer',
      };

  return (
    <WebsiteLink to={path}>
      <div
        css={[styling.common.headerStyle, isDL ? newActiveState : oldActiveState, linkStyles]}
        data-gtmid="nav-tab"
      >
        {displayText}
      </div>
    </WebsiteLink>
  );
};

type DesktopSubNavProps = {
  clubName?: string;
};

const DesktopSubNav: React.FC<DesktopSubNavProps> = ({ clubName }) => {
  const clubCmsData = use(ClubCmsContent);
  const globalCmsData = use(GlobalCmsContent);
  const { styling, language, isGlobalPage, buildType, isDL } = use(SiteContext);

  const navigationPagesToDisplay = isGlobalPage
    ? globalCmsData.sharedContent?.navigationPagesToDisplay
    : clubCmsData.navigationMenuContent?.navigationPagesToDisplay;

  return (
    <Location>
      {({ location }) => (
        <div css={subNavWrapperStyling(styling, isDL)}>
          <div css={atUnitTextStyling(styling, isDL)}>
            {isGlobalPage || !clubName ? (
              <TranslatedStaticText
                lookupKey="atUnit"
                params={{ unitName: getClubDisplayValues(buildType).displayName }}
              />
            ) : (
              <TranslatedStaticText
                lookupKey="atClub"
                params={{
                  clubName: clubNameDependingOnBuildTypeAndLanguage(clubName, buildType, language),
                }}
              />
            )}
          </div>
          {navigationPagesToDisplay?.mainPages && (
            <DesktopSubNavMainPageLinks
              pagesToDisplay={navigationPagesToDisplay?.mainPages}
              language={language}
              location={location}
              isGlobalPage={isGlobalPage}
            />
          )}
          {navigationPagesToDisplay?.secondaryPages && (
            <DesktopSubNavSecondaryPageLinks
              pagesToDisplay={navigationPagesToDisplay?.secondaryPages}
              language={language}
              location={location}
            />
          )}
        </div>
      )}
    </Location>
  );
};

export default DesktopSubNav;
